import { Link, useRouterState } from "@tanstack/react-router";
import {
  Activity,
  ClipboardList,
  Cpu,
  Droplets,
  Eye,
  FileBarChart,
  Gauge,
  Languages,
  LogOut,
  MapPin,
  Menu,
  Settings,
  ShieldAlert,
} from "lucide-react";
import { useState, type ReactNode } from "react";
import { useSession } from "@/auth/session";
import type { Permission } from "@/auth/rbac";
import { useI18n } from "@/i18n/provider";
import type { TKey } from "@/i18n/dict";
import { cn } from "@/lib/utils";
import { Logo } from "./logo";
import { OrgSwitcher } from "./org-switcher";

interface NavItem {
  to: string;
  label: TKey;
  icon: typeof Gauge;
  /** The permission required to even SEE this section. */
  permission: Permission;
}

const NAV: NavItem[] = [
  { to: "/", label: "nav.dashboard", icon: Gauge, permission: "sites:read" },
  { to: "/sites", label: "nav.sites", icon: MapPin, permission: "sites:read" },
  { to: "/monitoring", label: "nav.monitoring", icon: Activity, permission: "sites:read" },
  { to: "/devices", label: "nav.devices", icon: Cpu, permission: "devices:read" },
  { to: "/refills", label: "nav.refills", icon: Droplets, permission: "refills:read" },
  { to: "/requests", label: "nav.requests", icon: ClipboardList, permission: "requests:read" },
  { to: "/reports", label: "nav.reports", icon: FileBarChart, permission: "reports:read" },
  { to: "/settings", label: "nav.settings", icon: Settings, permission: "members:read" },
];

function LangToggle() {
  const { lang, setLang, t } = useI18n();
  return (
    <button
      type="button"
      onClick={() => setLang(lang === "ar" ? "en" : "ar")}
      aria-label={t("lang.switchTo")}
      className="inline-flex items-center gap-1.5 rounded-full border border-chrome-rule px-3 py-1.5 font-mono text-[11px] font-medium text-chrome-muted transition-colors hover:bg-panel hover:text-chrome-text focus-visible:outline-2 focus-visible:outline-offset-2"
    >
      <Languages className="size-3.5" aria-hidden />
      {t("lang.toggleShort")}
    </button>
  );
}

/**
 * A read-only session is not an error, and it must not look like one — but it must be
 * impossible to miss, or the operator fills in a whole refill form and loses it on submit.
 */
function ReadOnlyBanner() {
  const { t } = useI18n();
  const { readOnly, me, activeOrg } = useSession();
  if (!readOnly) return null;

  const impersonated = me?.impersonated === true;

  return (
    <div
      role="status"
      className={cn(
        "flex items-center justify-center gap-2 px-4 py-2 text-center text-xs font-medium",
        impersonated ? "bg-ink text-cream" : "bg-low-wash text-low",
      )}
    >
      {impersonated ? (
        <Eye className="size-3.5 shrink-0" aria-hidden />
      ) : (
        <ShieldAlert className="size-3.5 shrink-0" aria-hidden />
      )}
      {impersonated
        ? t("org.impersonating")
        : activeOrg?.status === "suspended"
          ? t("org.suspended")
          : null}
    </div>
  );
}

/** The nav rail's links, shared by the desktop rail and the mobile drawer. */
function NavLinks({
  items,
  pathname,
  onNavigate,
}: {
  items: NavItem[];
  pathname: string;
  onNavigate?: () => void;
}) {
  const { t } = useI18n();
  return (
    <nav aria-label={t("nav.dashboard")} className="flex flex-col gap-1">
      {items.map((item) => {
        const active = item.to === "/" ? pathname === "/" : pathname.startsWith(item.to);
        const Icon = item.icon;
        return (
          <Link
            key={item.to}
            to={item.to}
            onClick={onNavigate}
            aria-current={active ? "page" : undefined}
            className={cn(
              "flex items-center gap-3 rounded-[8px] px-3 py-2.5 text-sm transition-colors",
              "focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-cream",
              active
                ? // The active item is a slab of the brand panel with cream text — lit,
                  // and unmistakably the page you are on.
                  "bg-paper font-medium text-cream"
                : "text-dim hover:bg-paper/60 hover:text-cream",
            )}
          >
            <Icon className={cn("size-[18px] shrink-0", active ? "text-cream" : "text-faint")} aria-hidden />
            {t(item.label)}
          </Link>
        );
      })}
    </nav>
  );
}

export function AppShell({ children }: { children: ReactNode }) {
  const { t } = useI18n();
  const { can, me, signOut } = useSession();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const [drawerOpen, setDrawerOpen] = useState(false);

  // A nav item the role cannot use is not rendered. Not because hiding it is security —
  // the server decides that — but because a link that always 403s is a broken product.
  const items = NAV.filter((item) => can(item.permission));

  // The rail contents, reused verbatim by the fixed desktop rail and the mobile drawer, so
  // the two can never drift.
  const rail = (
    <div className="flex h-full flex-col gap-6 p-4">
      <Link
        to="/"
        onClick={() => setDrawerOpen(false)}
        className="shrink-0 px-2 text-cream focus-visible:outline-2 focus-visible:outline-offset-4"
        aria-label={t("app.name")}
      >
        <Logo className="h-6" />
      </Link>

      <div className="px-1">
        <OrgSwitcher />
      </div>

      <div className="flex-1 overflow-y-auto">
        <NavLinks items={items} pathname={pathname} onNavigate={() => setDrawerOpen(false)} />
      </div>

      {/* Who you are, and the way out — anchored to the foot of the rail. */}
      <div className="border-t border-chrome-rule pt-3">
        <div className="mb-2 flex items-center justify-between gap-2 px-2">
          <span className="min-w-0 truncate text-xs text-dim">{me?.user.name}</span>
          <LangToggle />
        </div>
        <button
          type="button"
          onClick={() => void signOut()}
          className="flex w-full items-center gap-3 rounded-[8px] px-3 py-2.5 text-sm text-dim transition-colors hover:bg-paper/60 hover:text-cream focus-visible:outline-2"
        >
          <LogOut className="size-[18px] shrink-0 text-faint" aria-hidden />
          {t("nav.signOut")}
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-dvh bg-canvas lg:grid lg:grid-cols-[16rem_1fr]">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:m-2 focus:rounded-[6px] focus:bg-ink focus:px-4 focus:py-2 focus:text-cream"
      >
        {t("nav.skipToContent")}
      </a>

      {/* ── the rail (lg+): fixed to the INLINE-START edge — right in Arabic, left in
             English. It stays plum, so the app is recognisably the same product as the
             marketing site. ── */}
      <aside className="on-chrome sticky top-0 hidden h-dvh border-e border-chrome-rule bg-ink lg:block">
        {rail}
      </aside>

      {/* ── the mobile top bar + drawer (below lg) ── */}
      <header className="on-chrome sticky top-0 z-30 flex h-14 items-center gap-3 border-b border-chrome-rule bg-ink px-4 lg:hidden">
        <button
          type="button"
          onClick={() => setDrawerOpen(true)}
          aria-label={t("nav.dashboard")}
          className="rounded-[8px] p-1.5 text-chrome-muted hover:bg-panel hover:text-chrome-text focus-visible:outline-2"
        >
          <Menu className="size-5" aria-hidden />
        </button>
        <Link to="/" className="text-cream" aria-label={t("app.name")}>
          <Logo className="h-5" />
        </Link>
        <div className="flex-1" />
        <LangToggle />
      </header>

      {drawerOpen ? (
        <div className="fixed inset-0 z-40 lg:hidden">
          <button
            type="button"
            aria-label={t("common.close")}
            className="absolute inset-0 bg-ink/50 backdrop-blur-[1px]"
            onClick={() => setDrawerOpen(false)}
          />
          <aside className="on-chrome absolute inset-y-0 start-0 w-[16rem] max-w-[80vw] border-e border-chrome-rule bg-ink">
            {rail}
          </aside>
        </div>
      ) : null}

      {/* ── the content column ── */}
      <div className="flex min-w-0 flex-col">
        <ReadOnlyBanner />
        <main id="main" className="mx-auto w-full max-w-[1400px] flex-1 p-4 md:p-6 lg:p-8">
          {children}
        </main>
      </div>
    </div>
  );
}

/** Page title + actions. Every screen opens the same way, so the eye knows where to land. */
export function PageHeader({
  title,
  description,
  actions,
}: {
  title: string;
  description?: string;
  actions?: ReactNode;
}) {
  return (
    <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
      <div className="flex min-w-0 flex-col gap-1">
        <h1 className="text-xl font-semibold text-text weight-display">{title}</h1>
        {description ? <p className="text-sm text-muted">{description}</p> : null}
      </div>
      {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
    </div>
  );
}
